import makeStyles from '@mui/styles/makeStyles';
import React from "react";
import {useTranslation} from "react-i18next";
import {NavLink} from "react-router-dom";
import logo from 'common/assets/logo-with-name.webp';
import {color, font, type} from "common/tokens";
import Card from "components/redesign/Card";
import background from 'views/signup/signInBackgroundImage.webp';

// The shell behind every screen you meet before signing in. The photograph runs
// full bleed under a scrim dark enough to carry white text, and the form sits on
// one card over it — the three screens used to hand-roll a Grid photo-split each,
// with their own copy of the palette.
const useStyles = makeStyles(theme => ({
  // Scrim and photograph are layered on one element: as two nested boxes the
  // inner `100%` had nothing to resolve against, so the gradient stopped at the
  // content and left the bottom of the photograph bare.
  // The photograph is portrait (1440x2160) in a landscape frame, so `cover`
  // crops it hard — held near the top, where the setting reads, rather than
  // centred on a face the card would then cut in half.
  page: {
    minHeight: '100%',
    background: `
      linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.62) 55%, rgba(0, 0, 0, 0.86) 100%),
      url(${background}) center 22% / cover no-repeat
    `,
    backgroundColor: color.TEXT,
    display: 'flex',
    flexDirection: 'column',
    padding: '32px 24px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px',
    },
  },
  // Auto margins centre the card when there is room and collapse when there is
  // not, so a tall step scrolls instead of having its ends cut off.
  content: {
    margin: 'auto',
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  card: {
    width: '100%',
    padding: '28px 32px 26px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: '22px 18px 20px',
    },
  },
  logo: {
    width: 118,
    display: 'block',
    margin: '0 auto 20px',
  },
  legal: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  link: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.SURFACE,
    textDecoration: 'none',
    opacity: 0.82,
    '&:hover': {
      opacity: 1,
      textDecoration: 'underline',
    },
  },
  divider: {
    ...type.meta,
    color: color.SURFACE,
    opacity: 0.4,
  },
}));

const AuthLayout = ({children}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const legal = [
    {to: '/privacypolicy', label: t('privacyPolicy')},
    {to: '/termsofservice', label: t('termsOfService')},
    {to: '/codeofconduct', label: t('codeOfConduct')},
  ];

  return (
    <div className={classes.page}>
      <div className={classes.content}>
      <Card variant={'panel'} className={classes.card}>
        <img src={logo} alt={'Mentor To Go'} className={classes.logo}/>
        {children}
      </Card>

      <nav className={classes.legal} aria-label={t('moreInfo')}>
        {legal.map((item, index) => (
          <React.Fragment key={item.to}>
            {index > 0 && <span className={classes.divider} aria-hidden={true}>|</span>}
            <NavLink to={item.to} className={classes.link}>{item.label}</NavLink>
          </React.Fragment>
        ))}
      </nav>
      </div>
    </div>
  );
};

export default AuthLayout;
